import { useMemo, useRef, useState } from 'react';

import { LinePath } from '@visx/shape';
import { AreaClosed } from '@visx/shape';
import { scaleLinear, scaleTime } from '@visx/scale';
import { extent } from 'd3-array';

import { useTheme, Box } from '@simplywallst/ui-core';

import { getTimeSeriesPeriodMap } from '@/utilities/timeSeriesPeriodMap';
import type { Period } from '@/utilities/timeSeriesPeriodMap';

import useWindowSize from '@/hooks/useWindowSize';
import { useDebounce } from '@simplywallst/hooks/useDebounce';
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';

import { Chart } from './styled';

interface Props {
  period?: Period;
  height?: number;
  entries: Record<number, number>;
  keys: number[];
}

export const MiniChart = ({
  period = '1Y',
  height = 40,
  entries,
  keys,
}: Props) => {
  const theme = useTheme();
  const padding = theme.x0_75;
  const [minY, maxY] = useMemo(() => extent(Object.values(entries)), [entries]);
  const [width, setWidth] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debouncedSize = useDebounce(useWindowSize(), 1000);
  const periodMap = getTimeSeriesPeriodMap(new Date());

  useIsomorphicLayoutEffect(() => {
    const { current } = wrapperRef;

    if (current) {
      const nextWidth = current.getBoundingClientRect().width;
      if (nextWidth > 0 && nextWidth !== width) {
        setWidth(nextWidth);
      }
    }
  }, [debouncedSize, width]);

  const xScale = scaleTime<number>({
    range: [padding, width],
    domain: [
      keys.length > 0 ? keys[0] : periodMap[period],
      keys.length > 0 ? keys[keys.length - 1] : periodMap.NOW,
    ],
  });

  const yScale = scaleLinear<number>({
    range: [padding, height - padding],
    domain: [maxY || 0, minY || 0],
  });

  return (
    <Box position="absolute" width="100%" ref={wrapperRef}>
      <Chart y="0" x="0" width={width} height={height}>
        <LinePath
          data={keys}
          x={(d) => xScale(d || 0)}
          y={(d) => yScale(entries[d] || 0)}
          stroke={theme.color.brand01}
          strokeWidth={1}
          strokeLinecap="round"
        />
        <AreaClosed
          data={keys}
          x={(d) => xScale(d || 0)}
          y={(d) => yScale(entries[d] || 0)}
          yScale={yScale} // This prop is useless but VX TS required it
          fill={'url(#chart-gradient)'}
          fillOpacity="0.5"
          y0={() => height + padding}
        />
      </Chart>
    </Box>
  );
};
